import React from 'react';
import { Text, Flex, Box } from '@chakra-ui/react';
import { OutlineCard as AlembicOutlineCard } from '@morressier/alembic';
import GroupRenderer from './FormFieldGeneric/GroupRenderer';
import { GenericSubmissionField } from './FormFieldGeneric/GenericSubmissionField';
import { useSubmissionFields } from '../../../hooks/useSubmissionFields';
import { FormFieldRadio } from '@publishing-engine/domain/types/article';
import { isDependencySatisfied } from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/asDependentFieldComponent';

export const OutlineCard: React.FC<{ children: React.ReactNode }> = ({
  children
}) => (
  <AlembicOutlineCard
    borderless
    borderRadius="none"
    flexDirection="column"
    justifyContent="space-between"
    padding={4}
    gap={4}
  >
    {children}
  </AlembicOutlineCard>
);
interface Props {
  showTitle?: boolean;
  isUpdateRequired?: boolean;
  isRevisionReview?: boolean;
  isShow?: boolean;
}
export const PreferencesTab: React.FC<Props> = ({
  showTitle = true,
  isUpdateRequired = true,
  isRevisionReview = false,
  isShow = false
}) => {
  const { getFieldState } = useSubmissionFields('submission');
  const licensesAndPermissions = getFieldState('LicensesAndPermissions');

  const allPreferenceFields = [
    'CommentForEditor',
    'CoAuthorApproval',
    'PublishingModel',
    'OpenAccessChoice',
    'OpenAccessChoiceWorldBankACountry',
    'OpenAccessChoiceWorldBankBCountry',
    'OpenAccessChoiceReviewerDiscountRate',
    'PreferenceImmediatePublication',
    'LicensesAndPermissions',
    'LicensesAndPermissionsReproducedContent',
    'LicensesAndPermissionsFiles',
    'TransparentPeerReview',
    'DoubleAnonymousPeerReview',
    'RecommendedReviewers',
    'OpposedReviewers',
    'NoveltyStatement',
    'DataAvailability',
    'DataAvailabilityOpenlyAvailableUrl',
    'DataAvailabilityEmbargoUrl',
    'DataAvailabilityEmbargoDate',
    'DataUnavailabilityReason',
    'DataAvailabilityStatement',
    'ClinicalTrials',
    'EthicsStatement',
    'Funding',
    'FundingGeneric',
    'AcceleratedPublication',
    'IPEMMembership',
    'SubjectArea',
    'PreviouslySubmitted',
    'ConflictOfInterestForAuthor',
    'AITools',
    'Preprint',
    'SharedDataset',
    'GeneratedDataset',
    'UtilizedDataset',
    'EthicalApproval',
    'EthicsApproval',
    'CopyrightLicensingForm',
  ];

  const hasAnyField = allPreferenceFields.some(fieldName => {
    const ffd = getFieldState(fieldName);
    if (!ffd) return false;

    // Check external field dependency (same logic as GenericSubmissionField)
    if ('externalFieldDependency' in ffd.formFieldDefinition.params) {
      const expectedValue =
        ffd.formFieldDefinition.params.externalFieldDependency?.value;
      const currentValue = ffd.externalDependency?.pathValue;
      if (!isDependencySatisfied(currentValue, expectedValue)) {
        return false;
      }
    }

    return true;
  });

  return (
    <Flex gap={4} flexDirection="column">
      {/* Page heading */}
      {hasAnyField && !showTitle && isShow && (
        <Text textStyle="headingSection">Preferences</Text>
      )}
      {hasAnyField && showTitle && (
        <Flex flexDirection="column" alignItems="flex-start" gap={2}>
          <Text textStyle="headingSection">About</Text>
          <Text textStyle="textBody">
            Verify this submission's preferences, statements and author
            consents.
          </Text>
        </Flex>
      )}

      <GenericSubmissionField
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fieldName="CommentForEditor"
      />

      <GenericSubmissionField
        fieldName="CoAuthorApproval"
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
      />

      {/* Publishing Preferences */}
      <GroupRenderer
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fields={[
          {
            name: 'PublishingModel',
            renderMethod: element => element
          },
          {
            name: 'OpenAccessChoice',
            renderMethod: element => element
          },
          {
            name: 'OpenAccessChoiceWorldBankACountry',
            renderMethod: element => element
          },
          {
            name: 'OpenAccessChoiceWorldBankBCountry',
            renderMethod: element => element
          },
          {
            name: 'OpenAccessChoiceReviewerDiscountRate',
            renderMethod: element => element
          },
          {
            name: 'PreferenceImmediatePublication',
            renderMethod: element => element
          }
        ]}
      >
        {renderedFields => renderedFields}
      </GroupRenderer>

      <GroupRenderer
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fields={[
          {
            name: 'LicensesAndPermissions',
            renderMethod: element => element,
            customCopies: {
              fieldLabel:
                (licensesAndPermissions?.formFieldDefinition as FormFieldRadio)
                  ?.params?.labels.input?.author || undefined,
              diffModalTitle:
                (licensesAndPermissions?.formFieldDefinition as FormFieldRadio)
                  ?.params?.labels.input?.author || undefined
            }
          },
          {
            name: 'LicensesAndPermissionsReproducedContent',
            renderMethod: element => element
          },
          {
            name: 'LicensesAndPermissionsFiles',
            renderMethod: element => (
              <Box id={'LicensesAndPermissionsFiles'}>{element}</Box>
            )
          }
        ]}
        showSubtitle={false}
      >
        {renderedFields => (
          <Flex gap={4} flexDirection="column">
            {renderedFields}
          </Flex>
        )}
      </GroupRenderer>
      <GroupRenderer
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fields={[
          {
            name: 'TransparentPeerReview',
            renderMethod: element => element
          },
          {
            name: 'DoubleAnonymousPeerReview',
            renderMethod: element => element
          },
          {
            name: 'RecommendedReviewers',
            renderMethod: element => element
          },
          {
            name: 'OpposedReviewers',
            renderMethod: element => element
          }
        ]}
      >
        {renderedFields => (
          <Flex gap={4} flexDirection="column">
            {renderedFields}
          </Flex>
        )}
      </GroupRenderer>

      {/* Motivation to Publish */}
      <GroupRenderer
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fields={[
          {
            name: 'NoveltyStatement',
            renderMethod: element => element
          }
        ]}
      >
        {renderedFields => (
          <Flex gap={4} flexDirection="column">
            {renderedFields}
          </Flex>
        )}
      </GroupRenderer>

      {/* ----- Data Availability ----- */}
      <GroupRenderer
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fields={[
          {
            name: 'DataAvailability',
            renderMethod: element => element
          },
          {
            name: 'DataAvailabilityOpenlyAvailableUrl',
            renderMethod: element => element
          },
          {
            name: 'DataAvailabilityEmbargoUrl',
            renderMethod: element => element
          },
          {
            name: 'DataAvailabilityEmbargoDate',
            renderMethod: element => element
          },
          {
            name: 'DataUnavailabilityReason',
            renderMethod: element => element
          },
          {
            name: 'DataAvailabilityStatement',
            renderMethod: element => element
          }
        ]}
      >
        {renderedFields => (
          <Flex gap={4} flexDirection="column">
            {renderedFields}
          </Flex>
        )}
      </GroupRenderer>

      {/* Ethics */}
      <GroupRenderer
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fields={[
          {
            name: 'ClinicalTrials',
            renderMethod: element => element
          },
          {
            name: 'EthicsStatement',
            renderMethod: element => element
          }
        ]}
      >
        {renderedFields => (
          <Flex gap={4} flexDirection="column">
            {renderedFields}
          </Flex>
        )}
      </GroupRenderer>

      <GenericSubmissionField
        fieldName="Funding"
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
      />
      <GenericSubmissionField
        fieldName="FundingGeneric"
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
      />

      {/* Additional Preferences */}
      <GroupRenderer
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fields={[
          {
            name: 'AcceleratedPublication',
            renderMethod: el => <OutlineCard>{el}</OutlineCard>
          },
          {
            name: 'IPEMMembership',
            renderMethod: el => <OutlineCard>{el}</OutlineCard>
          },
          {
            name: 'SubjectArea',
            renderMethod: el => <OutlineCard>{el}</OutlineCard>
          }
        ]}
      >
        {renderedFields => (
          <Flex gap={4} flexDirection="column">
            {renderedFields}
          </Flex>
        )}
      </GroupRenderer>

      <GroupRenderer
        isUpdateRequired={isUpdateRequired}
        isRevisionReview={isRevisionReview}
        fields={[
          {
            name: 'PreviouslySubmitted',
            renderMethod: el => el
          },
          {
            name: 'ConflictOfInterestForAuthor',
            renderMethod: el => el
          },
          {
            name: 'AITools',
            renderMethod: el => el
          },
          {
            name: 'Preprint',
            renderMethod: el => el
          },
          {
            name: 'SharedDataset',
            renderMethod: el => el
          },
          {
            name: 'GeneratedDataset',
            renderMethod: el => <Box id="Datasets Statement">{el}</Box>
          },
          {
            name: 'UtilizedDataset',
            renderMethod: el => el
          },
          {
            name: 'EthicalApproval',
            renderMethod: el => el
          },
          {
            name: 'EthicsApproval',
            renderMethod: el => el
          },
          {
            name: 'CopyrightLicensingForm',
            renderMethod: el => el
          }
        ]}
      >
        {renderedFields => (
          <Flex gap={4} flexDirection="column">
            {renderedFields}
          </Flex>
        )}
      </GroupRenderer>
    </Flex>
  );
};
